import { defineComponent, ref, provide, h, inject, computed } from 'vue'
import { IRequestExtension } from '~/src/interface'
import AppConfig from '~/src/app.config'
import BrowserChorme from '@/assets/icon/browser-chorme.svg'
import BrowserEdge from '@/assets/icon/browser-edge.svg'
import BrowserFirefox from '@/assets/icon/browser-firefox.svg'
import BrowserSafari from '@/assets/icon/browser-safari.svg'
import { useSeoConfigHook } from '@/utils/hooks'
import ShareMetaInfo from './shareMetaInfo.vue'
import Favicon from './favicon.vue'
import { useStoreHooks } from '@/store/hook'
import { TENANT_ID_MAP, checkIsProduction } from '@/utils'

export default defineComponent({
  components: {
    DesktopScript: {
      render: () => {
        const reqExtension: IRequestExtension | undefined = inject('reqExtension')
        // 桌面浏览器屏幕尺寸大小时跳转到手机端版本
        const script = `(function(win,isInWhiteList,toMobileWidth){
          var getUrlPath = function(url){ var path = url.split('://')[1]; return path.slice(path.indexOf('/'));};
          var toUrl = function(path){
            win.location = path;
          };
          var site = '/${reqExtension?.site}';
          var supposedPrefix = '/${reqExtension?.site}/mobile';
          var toMobileOrPC = function() {
            var path = getUrlPath(win.location.href);
            var clientWidth = win.document.documentElement.clientWidth;
            if(!isInWhiteList){
              if(clientWidth > toMobileWidth){
                if (path.indexOf(supposedPrefix) === 0){ toUrl(site + path.split(supposedPrefix)[1]); }
              } else {
                var route = path;
                if (path.indexOf(site) === 0) { route = path.slice(site.length) } 
                if (path.indexOf(supposedPrefix) !== 0){ toUrl(supposedPrefix + route );}
              }
            } 
          };
          win.onresize = function(){ toMobileOrPC();};
          toMobileOrPC();
          window.prefix = '${reqExtension?.site}';
        })(window,${reqExtension?.isInWhiteList},${AppConfig?.toMobileWidth});`
        return h('script', {
          innerHTML: script
        })
      }
    },
    MobileScript: {
      render: () => {
        const reqExtension: IRequestExtension | undefined = inject('reqExtension')
        const isMobile = reqExtension?.path?.split('/')?.includes('mobile')
        if (isMobile) {
          const remUnit = AppConfig.designPx ? AppConfig.designPx / 10 : 75
          // 移动端rem适配不同手机尺寸代码： 1rem=10px
          let script = `!function(e,t){
            function n(){
              t.body ? t.body.style.fontSize=12*o+"px" : t.addEventListener("DOMContentLoaded",n)
            }
            function d(){
              var e=i.clientWidth/${remUnit};
              i.style.fontSize=e+"px"
            }
            var i=t.documentElement, o=e.devicePixelRatio||1;
            if(n(),d(),e.addEventListener("resize",d),e.addEventListener("pageshow",function(e){e.persisted&&d()}),o>=2){
              var a=t.createElement("body"),s=t.createElement("div");
              s.style.border=".5px solid transparent",a.appendChild(s),i.appendChild(a),1===s.offsetHeight&&i.classList.add("hairlines"),i.removeChild(a)
            }
          }(window, document);`
          script = script + `window.prefix = '${reqExtension?.site}';`
          return h('script', {
            innerHTML: script
          })
        } else {
          return null
        }
      }
    },
    GaScript: {
      render: () => {
        const reqExtension: IRequestExtension | undefined = inject('reqExtension')
        let code = ''
        if (checkIsProduction(reqExtension?.hostname || '')) {
          code = 'G-DTRH106XZH'
        } else {
          code = 'G-01TXQ4CGJK'
        }
        return h('script', {
          async: true,
          src: `https://www.googletagmanager.com/gtag/js?id=${code}`
        })
      }
    },
    GtagScript: {
      render: () => {
        const reqExtension: IRequestExtension | undefined = inject('reqExtension')
        let code = ''
        if (checkIsProduction(reqExtension?.hostname || '')) {
          code = 'G-DTRH106XZH'
        } else {
          code = 'G-01TXQ4CGJK'
        }
        // 移动端rem适配不同手机尺寸代码： 1rem=10px
        let script = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
        
          gtag('config', '${code}');
        `
        script = script + `window.prefix = '${reqExtension?.site}';`
        return h('script', {
          innerHTML: script
        })
      }
    },
    CompatibleScript: {
      // ie浏览器不兼容提示脚本
      render: () => {
        const reqExtension: IRequestExtension | undefined = inject('reqExtension')
        // 桌面浏览器屏幕尺寸大小时跳转到手机端版本
        const script = `(function(win){
          const isIE = win.ActiveXObject || "ActiveXObject" in win
          if (!isIE) {
            return
          }
                    
          const mask = document.createElement('div')
          mask.style.cssText = 'background: rgba(0, 0, 0, 0.4); position: fixed; z-index: 9999; top: 0; left: 0; width: 100vw; height: 100vh;';
          
          const modal = document.createElement('div')
          modal.classList.add('ie-error-modal')

          const header = document.createElement('div')
          header.classList.add('error-header')
          header.innerText="For a better Experience"
          modal.appendChild(header)

          const desc = document.createElement('div')
          desc.classList.add('error-desc')
          desc.innerText='Try using one of the recommended browsers below,as our site no longer supports internet explorer.'
          modal.appendChild(desc)

          const imageWrapper = document.createElement('div')
          imageWrapper.classList.add('icon-wrapper')

          const chormeIcon = document.createElement('img')
          chormeIcon.src='${BrowserChorme}'
          imageWrapper.appendChild(chormeIcon)
          const edgeIcon = document.createElement('img')
          edgeIcon.src='${BrowserEdge}'
          imageWrapper.appendChild(edgeIcon)
          const firefoxIcon = document.createElement('img')
          firefoxIcon.src='${BrowserFirefox}'
          imageWrapper.appendChild(firefoxIcon)
          const safariIcon = document.createElement('img')
          safariIcon.src='${BrowserSafari}'
          imageWrapper.appendChild(safariIcon)

          modal.appendChild(imageWrapper)

          setTimeout(function () {
            const root = document.querySelector('#app')
            root.appendChild(mask)
            root.appendChild(modal)
          }, 2000)
        })(window,${reqExtension?.isInWhiteList});`
        return h('script', {
          innerHTML: script
        })
      }
    },
    ShareMetaInfo,
    Favicon
  },
  props: ['asyncData'],
  setup (props: any, context: any) {
    // 只会服务端渲染
    const { attrs } = context
    const { state } = useStoreHooks()
    const reqExtension: IRequestExtension = attrs.ctx.request.extension as IRequestExtension
    const isMobileBrowser = ref(reqExtension.isMobileBrowser)
    const template = ref(reqExtension.template)
    const { title, description } = useSeoConfigHook(reqExtension.path)
    // 传递给子的页面路由组件（可能是客户端渲染的组件）
    provide('reqExtension', reqExtension)
    provide('asyncData', props.asyncData.value)

    // detail 页面谷歌等网站分享使用meta标签
    const shareMetaInfo = props.asyncData.value.shareMetaInfo ? props.asyncData.value.shareMetaInfo : null
    const isArSite = computed(() => state.global.isArSite)
    const isMicro = computed(() => state.global.tenantId === TENANT_ID_MAP.micro)

    return {
      isMobileBrowser,
      template,
      title,
      description,
      BrowserSafari,
      shareMetaInfo,
      isArSite,
      isMicro
    }
  }
})
